import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
    Typography,
  Descriptions,
  Transfer,
  Tabs,
  List,
  Avatar,
  Tooltip,
  Badge,
  Spin,
  Select,
  Divider,
  Tree,
  theme as antdTheme
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { DataNode } from 'antd/es/tree';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  SettingOutlined,
  TeamOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import {
  Role,
  Permission,
  User,
  CreateRoleRequest,
  UpdateRoleRequest,
  AssignPermissionsToRoleRequest,
  AssignUsersToRoleRequest,
  PermissionTreeNode,
  PermissionTreeResponse
} from '../../types';
import { roleApi, permissionApi, userApi } from '../../services/api';
import { usePermissionCheck, PermissionGuard } from '../../context/PermissionContext';
import { useTheme } from '../../context/ThemeContext';

const { Title } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;



const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState<boolean>(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState<boolean>(false);
  const [isPermissionModalVisible, setIsPermissionModalVisible] = useState<boolean>(false);
  const [isUserModalVisible, setIsUserModalVisible] = useState<boolean>(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [detailRole, setDetailRole] = useState<Role | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // 权限和用户数据
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [permissionTree, setPermissionTree] = useState<PermissionTreeNode[]>([]);
  const [rolePermissions, setRolePermissions] = useState<Permission[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [roleUsers, setRoleUsers] = useState<User[]>([]);
  const [permissionLoading, setPermissionLoading] = useState<boolean>(false);
  const [userLoading, setUserLoading] = useState<boolean>(false);
  const [checkedPermissionKeys, setCheckedPermissionKeys] = useState<string[]>([]);



  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const {
    canViewRoles,
    canCreateRoles,
    canEditRoles,
    canDeleteRoles,
    canAssignPermissions,
    canAssignRoles
  } = usePermissionCheck();

  const { theme: appTheme } = useTheme();
  const { token } = antdTheme.useToken();

  // 权限分类选项
  const categoryOptions = [
    { label: '仪表盘', value: 'dashboard' },
    { label: '用户管理', value: 'user' },
    { label: '角色管理', value: 'role' },
    { label: '权限管理', value: 'permission' },
    { label: 'API管理', value: 'api' },
    { label: '日志管理', value: 'log' },
    { label: '系统管理', value: 'system' }
  ];

  // 权限类型选项
  const typeOptions = [
    { label: '页面权限', value: 'page' },
    { label: '操作权限', value: 'action' },
    { label: '按钮权限', value: 'button' }
  ];

  // 获取角色列表
  const fetchRoles = async (page: number = currentPage, size: number = pageSize) => {
    try {
      setLoading(true);
      const response = await roleApi.getRoles(page, size);
      setRoles(response.data.roles || []);
      setTotal(response.data.total || 0);
    } catch (error: any) {
      console.error('获取角色列表失败:', error);
      message.error(error.response?.data?.message || '获取角色列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取所有权限
  const fetchAllPermissions = async () => {
    try {
      const response = await permissionApi.getAllPermissions();
      setAllPermissions(response.data.permissions || []);
    } catch (error: any) {
      console.error('获取权限列表失败:', error);
    }
  };

  // 获取权限树
  const fetchPermissionTree = async () => {
    try {
      const response = await permissionApi.getPermissionTree();
      setPermissionTree(response.data.tree || []);
    } catch (error: any) {
      console.error('获取权限树失败:', error);
    }
  };

  // 获取所有用户
  const fetchAllUsers = async () => {
    try {
      const response = await userApi.getUsers({ page: 1, page_size: 1000 }); // 获取所有用户
      setAllUsers(response.data.users || []);
    } catch (error: any) {
      console.error('获取用户列表失败:', error);
    }
  };

  useEffect(() => {
    if (canViewRoles()) {
      fetchRoles();
      fetchAllPermissions();
      fetchPermissionTree();
      fetchAllUsers();
    }
  }, [currentPage, pageSize]);

  // 获取角色权限
  const fetchRolePermissions = async (roleId: number) => {
    try {
      setPermissionLoading(true);
      const response = await roleApi.getRolePermissions(roleId);
      const permissions = response.data.permissions || [];
      setRolePermissions(permissions);
      // 设置树形控件的选中状态
      setCheckedPermissionKeys(permissions.map(p => p.id.toString()));
    } catch (error: any) {
      console.error('获取角色权限失败:', error);
      message.error('获取角色权限失败');
    } finally {
      setPermissionLoading(false);
    }
  };

  // 获取角色用户
  const fetchRoleUsers = async (roleId: number) => {
    try {
      setUserLoading(true);
      const response = await roleApi.getRoleUsers(roleId);
      setRoleUsers(response.data.users || []);
    } catch (error: any) {
      console.error('获取角色用户失败:', error);
      message.error('获取角色用户失败');
    } finally {
      setUserLoading(false);
    }
  };

  // 创建角色
  const handleCreate = async (values: CreateRoleRequest) => {
    try {
      await roleApi.createRole(values);
      message.success('角色创建成功');
      setIsCreateModalVisible(false);
      createForm.resetFields();
      fetchRoles();
    } catch (error: any) {
      console.error('创建角色失败:', error);
      message.error(error.response?.data?.message || '创建角色失败');
    }
  };

  // 编辑角色
  const handleEdit = async (values: UpdateRoleRequest) => {
    if (!editingRole) return;
    
    try {
      await roleApi.updateRole(editingRole.id, values);
      message.success('角色更新成功');
      setIsEditModalVisible(false);
      setEditingRole(null);
      editForm.resetFields();
      fetchRoles();
    } catch (error: any) {
      console.error('更新角色失败:', error);
      message.error(error.response?.data?.message || '更新角色失败');
    }
  };

  // 删除角色
  const handleDelete = async (id: number) => {
    try {
      await roleApi.deleteRole(id);
      message.success('角色删除成功');
      fetchRoles();
    } catch (error: any) {
      console.error('删除角色失败:', error);
      message.error(error.response?.data?.message || '删除角色失败');
    }
  };

  // 查看角色详情
  const handleViewDetail = async (role: Role) => {
    setDetailRole(role);
    setIsDetailModalVisible(true);
  };

  // 编辑角色
  const handleEditClick = (role: Role) => {
    setEditingRole(role);
    editForm.setFieldsValue({
      code: role.code,
      name: role.name,
      description: role.description,
      is_system: role.is_system
    });
    setIsEditModalVisible(true);
  };

  // 权限分配
  const handlePermissionAssignment = (role: Role) => {
    setSelectedRole(role);
    fetchRolePermissions(role.id);
    setIsPermissionModalVisible(true);
  };

  // 用户分配
  const handleUserAssignment = (role: Role) => {
    setSelectedRole(role);
    fetchRoleUsers(role.id);
    setIsUserModalVisible(true);
  };

  // 保存权限分配
  const handleSavePermissions = async (checkedKeys: string[]) => {
    if (!selectedRole) return;
    
    try {
      const permissionIds = checkedKeys.map(key => parseInt(key));
      await roleApi.assignPermissionsToRole(selectedRole.id, { permission_ids: permissionIds });
      message.success('权限分配成功');
      
      // 更新本地状态
      const updatedPermissions = allPermissions.filter(permission => 
        permissionIds.includes(permission.id)
      );
      setRolePermissions(updatedPermissions);
      setCheckedPermissionKeys(checkedKeys);
    } catch (error: any) {
      console.error('分配权限失败:', error);
      message.error(error.response?.data?.message || '分配权限失败');
    }
  };

  // 保存用户分配
  const handleSaveUsers = async (targetKeys: string[]) => {
    if (!selectedRole) return;
    
    try {
      const userIds = targetKeys.map(key => parseInt(key));
      await roleApi.assignUsersToRole(selectedRole.id, { user_ids: userIds });
      message.success('用户分配成功');
      setIsUserModalVisible(false);
      setSelectedRole(null);
      setRoleUsers([]);
    } catch (error: any) {
      console.error('分配用户失败:', error);
      message.error(error.response?.data?.message || '分配用户失败');
    }
  };

  // 表格列定义
  const columns: ColumnsType<Role> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: '角色代码',
      dataIndex: 'code',
      key: 'code',
      width: 140,
      ellipsis: true,
      render: (code: string) => (
        <code
          style={{
            background: appTheme === 'dark' ? token.colorFillTertiary || '#222' : '#f5f5f5',
            color: appTheme === 'dark' ? token.colorText : undefined,
            padding: '2px 6px',
            borderRadius: '4px',
          }}
        >
          {code}
        </code>
      ),
    },
    {
      title: '角色名称',
      dataIndex: 'name',
      key: 'name',
      width: 130,
      ellipsis: true,
    },
    {
      title: '系统角色',
      dataIndex: 'is_system',
      key: 'is_system',
      width: 90,
      render: (isSystem: boolean) => (
        <Badge 
          status={isSystem ? 'success' : 'default'} 
          text={isSystem ? '是' : '否'} 
        />
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (description: string) => (
        <span title={description} style={{ cursor: 'pointer' }}>
          {description}
        </span>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          <PermissionGuard permission="role.edit">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditClick(record)}
              disabled={record.is_system}
            >
              编辑
            </Button>
          </PermissionGuard>
          <PermissionGuard permission="role.assign_permission">
            <Button
              type="link"
              size="small"
              icon={<SafetyCertificateOutlined />}
              onClick={() => handlePermissionAssignment(record)}
            >
              权限
            </Button>
          </PermissionGuard>
          <PermissionGuard permission="user.assign_role">
            <Button
              type="link"
              size="small"
              icon={<TeamOutlined />}
              onClick={() => handleUserAssignment(record)}
            >
              用户
            </Button>
          </PermissionGuard>
          <PermissionGuard permission="role.delete">
            <Popconfirm
              title="确定要删除这个角色吗？"
              description="删除后将无法恢复，请谨慎操作。"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
              disabled={record.is_system}
            >
              <Button
                type="link"
                size="small"
                danger
                disabled={record.is_system}
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          </PermissionGuard>
        </Space>
      ),
    },
  ];

  // 权限检查失败时的展示
  if (!canViewRoles()) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={3}>权限不足</Title>
          <p>您没有查看角色管理的权限</p>
        </div>
      </Card>
    );
  }



  // 将权限树转换为树形控件数据格式
  const convertToTreeData = (nodes: PermissionTreeNode[]): DataNode[] => {
    return nodes.map(node => {
      const dataNode: DataNode = {
        title: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: node.level === 0 ? 'bold' : 'normal' }}>
              {node.name}
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Tag color={getCategoryColor(node.category)}>
                {getCategoryLabel(node.category)}
              </Tag>
              <Tag color={getTypeColor(node.type)}>
                {getTypeLabel(node.type)}
              </Tag>
            </div>
          </div>
        ),
        key: node.id.toString(),
        children: node.children && node.children.length > 0 ? convertToTreeData(node.children) : undefined,
      };
      return dataNode;
    });
  };

  // 获取分类颜色
  const getCategoryColor = (category: string) => {
    const colorMap: { [key: string]: string } = {
      'dashboard': 'blue',
      'user': 'green',
      'role': 'orange',
      'permission': 'red',
      'api': 'purple',
      'log': 'cyan',
      'system': 'gray'
    };
    return colorMap[category] || 'default';
  };

  // 获取分类标签
  const getCategoryLabel = (category: string) => {
    const categoryMap: { [key: string]: string } = {
      'dashboard': '仪表盘',
      'user': '用户管理',
      'role': '角色管理',
      'permission': '权限管理',
      'api': 'API管理',
      'log': '日志管理',
      'system': '系统管理'
    };
    return categoryMap[category] || category;
  };

  // 获取类型颜色
  const getTypeColor = (type: string) => {
    const colorMap: { [key: string]: string } = {
      'module': 'blue',
      'page': 'green', 
      'action': 'orange',
      'button': 'red'
    };
    return colorMap[type] || 'default';
  };

  // 获取类型标签
  const getTypeLabel = (type: string) => {
    const typeMap: { [key: string]: string } = {
      'module': '模块权限',
      'page': '页面权限',
      'action': '操作权限', 
      'button': '按钮权限'
    };
    return typeMap[type] || type;
  };

  // 准备用户穿梭框数据
  const userTransferData = allUsers.map(user => ({
    key: user.id.toString(),
    title: user.name,
    description: `${user.username} - ${user.email}`,
  }));

  const userTargetKeys = roleUsers.map(user => user.id.toString());

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>角色管理</Title>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchRoles()}
                loading={loading}
              >
                刷新
              </Button>
              <PermissionGuard permission="role.create">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsCreateModalVisible(true)}
                >
                  新建角色
                </Button>
              </PermissionGuard>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={roles}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1040 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
          size="middle"
        />

        {/* 创建角色模态框 */}
        <Modal
          title="新建角色"
          open={isCreateModalVisible}
          onCancel={() => {
            setIsCreateModalVisible(false);
            createForm.resetFields();
          }}
          footer={null}
          width={600}
        >
          <Form
            form={createForm}
            layout="vertical"
            onFinish={handleCreate}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="code"
                  label="角色代码"
                  rules={[
                    { required: true, message: '请输入角色代码' },
                    { pattern: /^[a-z0-9_]+$/, message: '角色代码只能包含小写字母、数字和下划线' }
                  ]}
                >
                  <Input placeholder="如: operator" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="角色名称"
                  rules={[{ required: true, message: '请输入角色名称' }]}
                >
                  <Input placeholder="如: 运营管理员" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="description"
              label="角色描述"
            >
              <Input.TextArea rows={3} placeholder="描述角色的职责和功能" />
            </Form.Item>
            <Form.Item
              name="is_system"
              label="系统角色"
              valuePropName="checked"
              tooltip="系统角色不可删除和修改"
            >
              <Switch />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => {
                  setIsCreateModalVisible(false);
                  createForm.resetFields();
                }}>
                  取消
                </Button>
                <Button type="primary" htmlType="submit">
                  创建
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* 编辑角色模态框 */}
        <Modal
          title="编辑角色"
          open={isEditModalVisible}
          onCancel={() => {
            setIsEditModalVisible(false);
            setEditingRole(null);
            editForm.resetFields();
          }}
          footer={null}
          width={600}
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleEdit}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="code"
                  label="角色代码"
                  rules={[
                    { required: true, message: '请输入角色代码' },
                    { pattern: /^[a-z0-9_]+$/, message: '角色代码只能包含小写字母、数字和下划线' }
                  ]}
                >
                  <Input placeholder="如: operator" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="角色名称"
                  rules={[{ required: true, message: '请输入角色名称' }]}
                >
                  <Input placeholder="如: 运营管理员" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="description"
              label="角色描述"
            >
              <Input.TextArea rows={3} placeholder="描述角色的职责和功能" />
            </Form.Item>
            <Form.Item
              name="is_system"
              label="系统角色"
              valuePropName="checked"
              tooltip="系统角色不可删除和修改"
            >
              <Switch disabled />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => {
                  setIsEditModalVisible(false);
                  setEditingRole(null);
                  editForm.resetFields();
                }}>
                  取消
                </Button>
                <Button type="primary" htmlType="submit">
                  更新
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* 角色详情模态框 */}
        <Modal
          title="角色详情"
          open={isDetailModalVisible}
          onCancel={() => {
            setIsDetailModalVisible(false);
            setDetailRole(null);
          }}
          footer={[
            <Button key="close" onClick={() => {
              setIsDetailModalVisible(false);
              setDetailRole(null);
            }}>
              关闭
            </Button>
          ]}
          width={600}
        >
          {detailRole && (
            <Descriptions column={1} bordered>
              <Descriptions.Item label="ID">
                {detailRole.id}
              </Descriptions.Item>
              <Descriptions.Item label="角色代码">
                <code
                  style={{
                    background: appTheme === 'dark' ? token.colorFillTertiary || '#222' : '#f5f5f5',
                    color: appTheme === 'dark' ? token.colorText : undefined,
                    padding: '2px 6px',
                    borderRadius: '4px',
                  }}
                >
                  {detailRole.code}
                </code>
              </Descriptions.Item>
              <Descriptions.Item label="角色名称">
                {detailRole.name}
              </Descriptions.Item>
              <Descriptions.Item label="系统角色">
                <Badge 
                  status={detailRole.is_system ? 'success' : 'default'} 
                  text={detailRole.is_system ? '是' : '否'} 
                />
              </Descriptions.Item>
              <Descriptions.Item label="角色描述">
                {detailRole.description || '暂无描述'}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {new Date(detailRole.created_at).toLocaleString('zh-CN')}
              </Descriptions.Item>
              <Descriptions.Item label="更新时间">
                {new Date(detailRole.updated_at).toLocaleString('zh-CN')}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>

        {/* 权限分配模态框 */}
        <Modal
          title={`权限分配 - ${selectedRole?.name}`}
          open={isPermissionModalVisible}
          onCancel={() => {
            setIsPermissionModalVisible(false);
            setSelectedRole(null);
            setRolePermissions([]);
            setCheckedPermissionKeys([]);
          }}
          footer={[
            <Button key="cancel" onClick={() => {
              setIsPermissionModalVisible(false);
              setSelectedRole(null);
              setRolePermissions([]);
              setCheckedPermissionKeys([]);
            }}>
              取消
            </Button>,
            <Button key="save" type="primary" onClick={() => {
              handleSavePermissions(checkedPermissionKeys);
              setIsPermissionModalVisible(false);
              setSelectedRole(null);
            }}>
              保存
            </Button>
          ]}
          width={700}
        >
          <Spin spinning={permissionLoading}>
            <div style={{ marginBottom: 16 }}>
              <span style={{ fontWeight: 'bold', color: appTheme === 'dark' ? '#ffffff' : '#000000' }}>
                权限树结构
              </span>
              <span style={{ 
                marginLeft: 8, 
                color: appTheme === 'dark' ? '#999999' : '#999' 
              }}>
                请勾选要分配给该角色的权限
              </span>
            </div>
            {permissionTree.length > 0 ? (
              <Tree
                checkable
                treeData={convertToTreeData(permissionTree)}
                checkedKeys={checkedPermissionKeys}
                onCheck={(checkedKeys) => {
                  const keys = Array.isArray(checkedKeys) ? checkedKeys as string[] : checkedKeys.checked as string[];
                  setCheckedPermissionKeys(keys);
                }}
                defaultExpandAll
                showLine={{ showLeafIcon: false }}
                style={{ 
                  background: appTheme === 'dark' ? '#1f1f1f' : '#fafafa',
                  color: appTheme === 'dark' ? '#ffffff' : '#000000',
                  padding: '16px',
                  borderRadius: '6px',
                  border: appTheme === 'dark' ? '1px solid #434343' : '1px solid #d9d9d9',
                  maxHeight: '400px',
                  overflowY: 'auto'
                }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Title level={4} type="secondary">暂无权限数据</Title>
                <p>请先执行数据库迁移来初始化权限树结构</p>
              </div>
            )}
          </Spin>
        </Modal>

        {/* 用户分配模态框 */}
        <Modal
          title={`用户分配 - ${selectedRole?.name}`}
          open={isUserModalVisible}
          onCancel={() => {
            setIsUserModalVisible(false);
            setSelectedRole(null);
            setRoleUsers([]);
          }}
          footer={null}
          width={800}
        >
          <Spin spinning={userLoading}>
            <Transfer
              dataSource={userTransferData}
              targetKeys={userTargetKeys}
              showSearch
              listStyle={{
                width: 350,
                height: 400,
              }}
              operations={['分配', '移除']}
              titles={['可分配用户', '已分配用户']}
              render={(item: any) => `${item.title} - ${item.description}`}
              onChange={(targetKeys: any) => handleSaveUsers(targetKeys.map((key: any) => key.toString()))}
            />
          </Spin>
        </Modal>
      </Card>
    </div>
  );
};

export default RoleManagement; 